import React, {useRef, useState, useEffect, useContext} from 'react';
import {Popup, Scrollable, Layout, Icon, Flex, Tag, TagsList, EditableTag, Grid} from 'components';
import {over, modal, toggle, map, notify, useBuffer, useField, useNamedSelector, useSuggestionPopup, useSelector} from 'helpers';
import {units} from 'store';
import css from './number-unit.module.scss';

export function NumberUnit({className, ...props}) {
  const getByName = useSelector(units.getByName);
  const [{value}] = useField(props);
  const [unit, , {setValue}] = useField(props.unitName);
  const isNested = Boolean(useContext(Popup.Context));

  const [input, {focused}] = useBuffer({
    value: unit.value || '',
    onChange: buffer => {
      if (buffer.value.trim()) {
        const tag = getByName(buffer.value);
        setValue(tag?.name ?? buffer.value.trim());
      } else {
        setValue('');
      }
    }
  });

  const [popup, control] = useSuggestionPopup(Suggestions, {
    ...props,
    isNested,
    value: input.value,
    setValue,
    deps: [input.value, value],
    className: 'adjacent'
  });

  useEffect(() => {
    const label = control.ref.current?.closest('label');
    if (label) {
      label.id = popup.id;
      label.setAttribute('aria-haspopup', 'true');
    }
  }, [control.ref.current]);

  return Boolean(value) && (
    <EditableTag value={input.value} placeholder={focused ? 'enter..' : '+ unit'} className={css.tag} clear={() => setValue('')}>
      <input
        {...input}
        {...control}
        onFocus={over(input.onFocus, control.onFocus)}
        onChange={over(input.onChange, control.onChange)}
      />
    </EditableTag>
  );
}

function Suggestions({value, setValue, isNested, popup, ...props}) {
  const {predefined, custom} = useNamedSelector('units.units');
  return (
    <Scrollable>
      <Grid className={css('units', {isNested})} columns-4 tabIndex="0">
        { predefined.map(group =>
          <div key={group.name}>
            <div className="secondary">{ group.name }</div>
            <TagsList>
              { group.units.map(unit =>
                <Tag
                  key={unit.serverId}
                  onClick={over(() => setValue(unit.name), popup.close)}
                  selected={value === unit.name}
                  children={unit.name}
                />
              ) }
            </TagsList>
          </div>
        ) }
        <Grid.Width100 className={css.custom}>
          <Flex className={['small-gutter', 'secondary']}>
            <div>Custom</div>
            {!isNested &&
              <Icon name="pencil" onClick={over(modal(Settings), popup.close)} />
            }
          </Flex>
          <TagsList>
            { map(custom, unit =>
              <Tag
                key={unit.serverId}
                onClick={over(() => setValue(unit.name), popup.close)}
                selected={value === unit.name}
                children={unit.name}
              />
            ) }
          </TagsList>
        </Grid.Width100>
      </Grid>
    </Scrollable>
  );
}

function Settings({popup}) {
  const {custom} = useNamedSelector('units.units');
  const [marked, setMarked] = useState([]);
  const mark = id => setMarked(toggle(marked, id));
  const ref = useRef();
  ref.current = marked;
  useEffect(() => () => {
    if (ref.current.length) {
      units.dispatch.remove(ref.current);
    }
  }, []);
  return (
    <>
      <Layout.Header justified>
        <h5>Your custom units</h5>
        <Icon name="close" onClick={popup.close} />
      </Layout.Header>
      <TagsList>
        { map(custom, unit =>
          <UnitTag key={unit.serverId} mark={mark} marked={marked} unit={unit} />
        ) }
        <NewUnitTag />
      </TagsList>
    </>
  );
}

function UnitTag({unit, mark, marked}) {
  const getByName = useSelector(units.getByName);
  const [control, {focused}] = useBuffer({
    value: unit.name,
    onChange: ({value, valueBeforeFocus, update}) => {
      const match = getByName(value);
      if (value && match && match?.serverId !== unit.serverId) {
        // duplicate
        notify('Duplicate! This unit already exists.', {className: 'is-alert'});
        update({value: valueBeforeFocus});
      } else if (value) {
        units.dispatch.rename(unit.serverId, value)
      } else {
        // mark to remove
        update({value: valueBeforeFocus});
        mark(unit.serverId);
      }
    }
  });
  return (
    <EditableTag value={control.value} className={['white-tag', focused && 'focused']} maxWidth="20em">
      <input
        {...control}
        className={marked.includes(unit.serverId) ? 'deleted' : undefined}
        disabled={marked.includes(unit.serverId)}
      />
      <Icon
        className="secondary"
        name={marked.includes(unit.serverId) ? 'undo' : 'trash'}
        onClick={() => mark(unit.serverId)}
      />
    </EditableTag>
  );
}

function NewUnitTag() {
  const getByName = useSelector(units.getByName);
  const [control, {focused}] = useBuffer({
    value: '',
    onChange: ({value, update}) => {
      if (value.trim()) {
        if (getByName(value)) {
          notify('Duplicate! This unit already exists.', {className: 'is-alert'});
        } else {
          units.dispatch.upsert(value);
        }
      }
      update({value: ''});
    }
  });
  return (
    <EditableTag value={control.value} className={['white-tag', focused && 'focused']} maxWidth="20em" placeholder={focused ? 'Enter..' : '+ New'}>
      <input {...control} />
    </EditableTag>
  );
}
